/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { type ReactElement, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { ChatMessagesContainer, type ChatMessagesContainerRef } from '@coursera-web/cds-labs';
import classNames from 'classnames';

import { Fade, breakpoints } from '@coursera/cds-core';
import { ArrowUpIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachButton } from 'bundles/ai-coach-platform/components/building-blocks';
import ChatFooter from 'bundles/ai-coach-platform/components/patterns/chat/ChatFooter';
import ChatInputField from 'bundles/ai-coach-platform/components/patterns/chat/ChatInputField';
import ChatMessages, {
  type Props as ChatMessagesProps,
  type HideMessageType,
} from 'bundles/ai-coach-platform/components/patterns/chat/ChatMessages';
import type { FeedbackIssueOption } from 'bundles/ai-coach-platform/components/patterns/chat/FeedbackModal';
import {
  ChatHeader,
  ChatHeaderActionsMenu,
  ChatHistoryLoading,
  ChatRichInputField,
} from 'bundles/ai-coach-platform/components/patterns/chat/common';
import type { ChatPopupButton } from 'bundles/ai-coach-platform/components/patterns/chat/common/ChatHeader';
import ChatHeaderNotifications from 'bundles/ai-coach-platform/components/patterns/chat/common/ChatHeaderNotifications';
import { useCoachChatContext } from 'bundles/ai-coach-platform/components/patterns/chat/context/CoachChatContext';
import { useDynamicContent } from 'bundles/ai-coach-platform/components/patterns/chat/hooks/useDynamicContent';
import { useEarlierMessagesButton } from 'bundles/ai-coach-platform/components/patterns/chat/hooks/useEarlierMessagesButton';
import { useScrollPositionPreservation } from 'bundles/ai-coach-platform/components/patterns/chat/hooks/useScrollPositionPreservation';
import type { MemoryNotificationConfig } from 'bundles/ai-coach-platform/components/patterns/chat/notifications/types';
import type { GlobalState } from 'bundles/ai-coach-platform/components/patterns/chat/store/types';
import type {
  AnyFunction,
  CustomSendButtonProps,
  SendMessageFunc,
} from 'bundles/ai-coach-platform/components/patterns/chat/types';
import { CoachMode } from 'bundles/ai-coach-platform/constants';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';

import _t from 'i18n!nls/ai-coach-platform';

const styles = {
  conversationContainer: css`
    height: 100%;
  `,
  conversation: css`
    background-color: var(--cds-color-neutral-background-primary);
    height: 100%;
    width: 100%;
    display: flex;
    flex-flow: column;
    z-index: 10;
    border: none;
    border-radius: var(--cds-border-radius-200);
    position: relative;
    overflow-y: hidden; /* this hides the overflowing transform on dynamic composer content */
    container-type: size;
    container-name: coach-chat;

    ${breakpoints.down('xs')} {
      border-radius: 0;
    }

    ${breakpoints.up('md')} {
      [data-course-frame-2026] & {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
    }

    &.active {
      opacity: 1;
      transform: translateY(0);
      transition:
        opacity 0.3s ease,
        transform 0.3s ease;
    }

    &.hidden {
      z-index: -1;
      pointer-events: none;
      opacity: 0;
      transform: translateY(10px);
      transition:
        opacity 0.3s ease,
        transform 0.3s ease;
    }
  `,
  headerContainer: (isCustomHeader = false) => css`
    flex-grow: 0;
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);

    ${
      !isCustomHeader &&
      `@container coach-chat (max-height: 480px) {
      display: none;
    }`
    }
  `,
  composerContainer: css`
    flex-grow: 0;
    width: 100%;
    border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    transition: transform 0.4s ease-in-out;

    &.hidden {
      transform: translate3d(0, 100%, 0);
    }

    @container coach-chat (max-height: 480px) {
      padding-bottom: var(--cds-spacing-200);
    }
  `,
  composer: css`
    max-width: 770px;
    margin: 0 auto;
  `,
  footerContainer: (hideComposer = false) => css`
    flex-grow: 0;
    margin-top: 2px;
    background: var(--cds-color-neutral-background-primary);
    z-index: 1;
    display: flex;
    justify-content: center;

    ${hideComposer && 'border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);'}

    @container coach-chat (max-height: 480px) {
      display: none;
    }
  `,
  footer: css`
    max-width: 770px;
  `,
  earlierMessagesButton: css`
    position: absolute;
    top: var(--cds-spacing-800);
    left: 50%;
    transform: translateX(-50%) scale(1);
    z-index: 10;
    background-color: var(--cds-color-neutral-background-primary);
    box-shadow: 0 0 4px 0 var(--cds-color-darken-200);
    outline: 1px solid var(--cds-color-interactive-background-secondary-hover-weak);

    &:hover {
      background-color: var(--cds-color-interactive-background-secondary-hover-weak);
    }

    &:active {
      transform: translateX(-50%) scale(0.98);
    }
  `,
};

export type ConversationProps = {
  sendMessage: SendMessageFunc;
  toggleChat: AnyFunction;
  showStartNewChat?: boolean;
  emptyState?: ReactElement;
  senderPlaceHolder?: string;
  inputLabel?: string;
  showCloseButton?: boolean;
  disableInput?: boolean;
  disableSpeechToText?: boolean;
  speechToTextDisabledTooltip?: string;
  autofocus?: boolean;
  activeChatSessionId?: string;
  onTextInputChange?: (text: string) => void;
  handleNewSession?: () => void;
  isChatHistoryLoading?: boolean;
  customHeader?: ReactElement | null;
  customFooter?: ReactElement | null;
  hideFooter?: boolean;
  hideMessage?: HideMessageType;
  showSettings?: boolean;
  handleSettingsClick?: () => void;
  isCoachStudio?: boolean;
  showFullScreenButton?: boolean;
  toggleFullScreen?: () => void;
  isFullScreen?: boolean;
  showPositionToggle?: boolean;
  handlePositionToggleClick?: () => void;
  chatPopupButton?: ChatPopupButton;
  handlePopupButtonClick?: () => void;
  enableRichInputField?: boolean;
  renderCustomInputField?: ({ sendMessage }: { sendMessage: SendMessageFunc }) => ReactElement | null;
  renderCustomMessage?: ChatMessagesProps['renderCustomMessage'];
  renderCustomMessageLoader?: ChatMessagesProps['renderCustomMessageLoader'];
  renderCustomTemporaryStreamedMessage?: ChatMessagesProps['renderCustomTemporaryStreamedMessage'];
  chatMessageListAriaLabel?: string;
  renderTemporaryMessage?: () => ReactElement | null;
  showDynamicComposerContent?: boolean;
  dynamicComposerContent?: ReactElement | null;
  onDynamicComposerContentShown?: () => void;
  onDefaultComposerContentShown?: () => void;
  enableTextStreaming?: boolean;
  showSearchButton?: boolean;
  hideComposer?: boolean;
  searchButtonRef?: React.RefObject<HTMLButtonElement>;
  settingsButtonRef?: React.RefObject<HTMLButtonElement>;
  enableContinuousChat?: boolean;
  onFetchMoreChatHistory?: () => void;
  recommendedActionsLabel?: string;
  memoryNotificationConfig?: MemoryNotificationConfig;
  enableMemory?: boolean;
  brandLabel?: string;
  feedbackIssueOptions?: FeedbackIssueOption[];
  onFeedbackSubmitted?: (args: { messageId: string; userIssue?: string; userComments: string }) => void;
  /**
   * @deprecated Temporary prop for the Two-Panel XDP experiment (MERCH-2963). No longer functional
   * after the cds-labs-chat-components gate cleanup. Will be removed once consumers are updated.
   */
  renderCustomSendButton?: (props: CustomSendButtonProps) => ReactElement | null;
};

/**
 * Standalone component that handles all the conversations with Coach.
 */
const Conversation = ({
  senderPlaceHolder,
  inputLabel,
  showCloseButton = true,
  disableInput = false,
  disableSpeechToText = false,
  speechToTextDisabledTooltip = '',
  autofocus = true,
  showStartNewChat = true,
  sendMessage,
  toggleChat,
  activeChatSessionId,
  onTextInputChange,
  handleNewSession,
  emptyState,
  isChatHistoryLoading = false,
  customHeader,
  customFooter,
  hideFooter = false,
  hideMessage,
  showSettings = false,
  handleSettingsClick = () => {},
  isCoachStudio = false,
  showFullScreenButton = false,
  toggleFullScreen,
  isFullScreen = false,
  showPositionToggle,
  handlePositionToggleClick,
  chatPopupButton,
  handlePopupButtonClick,
  enableRichInputField = false,
  renderCustomInputField,
  renderCustomMessage,
  renderCustomMessageLoader,
  renderCustomTemporaryStreamedMessage,
  renderTemporaryMessage,
  chatMessageListAriaLabel,
  showDynamicComposerContent = false,
  dynamicComposerContent = null,
  onDynamicComposerContentShown = () => null,
  onDefaultComposerContentShown = () => null,
  enableTextStreaming = false,
  showSearchButton = false,
  hideComposer = false,
  searchButtonRef,
  settingsButtonRef,
  enableContinuousChat = false,
  onFetchMoreChatHistory,
  recommendedActionsLabel,
  memoryNotificationConfig,
  enableMemory = true,
  brandLabel,
  feedbackIssueOptions,
  onFeedbackSubmitted,
}: ConversationProps) => {
  const { isMessageLoading, isMessageStreaming, messagesCount } = useSelector((state: GlobalState) => ({
    isMessageLoading: state.behavior.messageLoader,
    isMessageStreaming: state.behavior.isMessageStreaming,
    messagesCount: state.messages.messages.length,
  }));
  const composerContainerRef = React.useRef(null);
  const cdsLabsContainerRef = React.useRef<ChatMessagesContainerRef>(null);
  const cdsLabsScrollElementRef = React.useRef<HTMLDivElement | null>(null);
  const { isSearchMode, searchTerm, suggestedActions, earlierMessagesAvailable, chatMode, settingsOpen } =
    useCoachChatContext();
  const track = useTracker();
  const trackingContext = useCoachTrackingContext()?.data;

  const defaultComposerComponent = React.useMemo(
    () => (
      <>
        {renderCustomInputField?.({ sendMessage }) ??
          (enableRichInputField ? (
            <ChatRichInputField
              sendMessage={sendMessage}
              placeholder={senderPlaceHolder || _t('Ask me anything')}
              disableInput={disableInput}
              disabled={isMessageLoading || isMessageStreaming}
              disableSpeechToText={disableSpeechToText}
              speechToTextDisabledTooltip={speechToTextDisabledTooltip}
              autofocus={autofocus}
              // only show suggested actions on non-empty chats because we have EmptyState for that case already
              suggestedActions={messagesCount > 0 ? suggestedActions : undefined}
              actionButtons={
                <ChatHeaderActionsMenu
                  onClose={toggleChat}
                  showStartNewChat={showStartNewChat}
                  showClose={showCloseButton}
                  handleNewSession={handleNewSession}
                  handleSettingsClick={handleSettingsClick}
                  showSettings={showSettings}
                  loading={isChatHistoryLoading || isMessageLoading || isMessageStreaming}
                  showFullScreenButton={showFullScreenButton}
                  toggleFullScreen={toggleFullScreen}
                  isFullScreen={isFullScreen}
                  showPositionToggle={showPositionToggle}
                  handlePositionToggleClick={handlePositionToggleClick}
                  chatPopupButton={chatPopupButton}
                  handlePopupButtonClick={handlePopupButtonClick}
                  showSearchButton={messagesCount > 0 ? showSearchButton : false}
                  enableContinuousChat={enableContinuousChat}
                />
              }
            />
          ) : (
            <ChatInputField
              inputLabel={inputLabel}
              sendMessage={sendMessage}
              placeholder={senderPlaceHolder || _t('Ask me anything')}
              disableInput={disableInput}
              autofocus={autofocus}
              onTextInputChange={onTextInputChange}
            />
          ))}
      </>
    ),
    [
      renderCustomInputField,
      sendMessage,
      enableRichInputField,
      senderPlaceHolder,
      disableInput,
      disableSpeechToText,
      speechToTextDisabledTooltip,
      autofocus,
      onTextInputChange,
      isMessageLoading,
      isMessageStreaming,
      suggestedActions,
      messagesCount,
      inputLabel,
      toggleChat,
      showStartNewChat,
      showCloseButton,
      handleNewSession,
      handleSettingsClick,
      showSettings,
      isChatHistoryLoading,
      showFullScreenButton,
      toggleFullScreen,
      isFullScreen,
      showPositionToggle,
      handlePositionToggleClick,
      chatPopupButton,
      handlePopupButtonClick,
      showSearchButton,
      enableContinuousChat,
    ]
  );

  const { composerComponent, composerHidden, onTransitionEnd } = useDynamicContent({
    defaultComposerComponent,
    dynamicComposerContent,
    showDynamicComposerContent,
    onDynamicComposerContentShown,
    onDefaultComposerContentShown,
  });

  const handleTransitionEnd = React.useCallback<(e: React.TransitionEvent) => void>(
    (e: React.TransitionEvent) => {
      // scope transitionEnd event to current container to avoid any bubbling events
      // to trigger this call
      if (e.target === composerContainerRef.current) {
        onTransitionEnd();
      }
    },
    [onTransitionEnd]
  );

  const { showEarlierMessagesButton, setShowEarlierMessagesButton, setOldestMessageRef } = useEarlierMessagesButton({
    earlierMessagesAvailable,
  });

  const shouldShowEarlierMessagesButton =
    enableContinuousChat && !isSearchMode() && messagesCount > 0 && showEarlierMessagesButton;

  const { captureScrollState, restoreScrollPosition } = useScrollPositionPreservation(cdsLabsScrollElementRef);

  // Track message count to detect when new messages are prepended
  const previousMessageCount = React.useRef(messagesCount);

  // Restore scroll position after new messages are prepended (when enableContinuousChat is true)
  React.useEffect(() => {
    if (enableContinuousChat && messagesCount > previousMessageCount.current) {
      // Use a small delay to ensure DOM has been updated
      requestAnimationFrame(() => {
        restoreScrollPosition();
      });
    }
    previousMessageCount.current = messagesCount;
  }, [messagesCount, enableContinuousChat, restoreScrollPosition]);

  const trackJumpToTop = useCallback<() => void>(() => {
    track('interact_coach_chat_search', {
      coachChatSearchInteractionType: 'click_jump_to_top',
      coachChatSearchQuery: searchTerm ?? '',
      coach: {
        mode: trackingContext?.mode ?? CoachMode.Basic,
      },
    });
  }, [track, trackingContext?.mode, searchTerm]);

  const trackJumpToBottom = useCallback<() => void>(() => {
    track('interact_coach', {
      coachInteractionType: 'click_jump_to_bottom',
      coach: {
        mode: trackingContext?.mode ?? CoachMode.Basic,
        location: chatMode === 'conversation' ? 'chat' : 'chat_search',
      },
    });

    // Coach eventing consolidation [COACH-2404]: dual-emit onto the new ClickCoachElement schema
    track('click_coach_element', {
      clickElementType: 'jump_to_bottom',
      coach: {
        mode: trackingContext?.mode ?? CoachMode.Basic,
        location: chatMode === 'conversation' ? 'chat' : 'chat_search',
      },
    });
  }, [track, trackingContext?.mode, chatMode]);

  const handleClickEarlierMessagesButton = useCallback<(e: React.MouseEvent<HTMLButtonElement>) => void>(
    (e) => {
      // If there are more earlier messages, the state will be updated to true from the onFetchMoreChatHistory call.
      setShowEarlierMessagesButton(false);

      // Capture current scroll state before fetching more messages,
      // so we can preserve user's scroll position
      captureScrollState();
      onFetchMoreChatHistory?.();
    },
    [onFetchMoreChatHistory, captureScrollState, setShowEarlierMessagesButton]
  );

  const messagesContent = (
    <>
      {isChatHistoryLoading && <ChatHistoryLoading />}
      {!isChatHistoryLoading && (
        <ChatMessages
          emptyState={emptyState}
          sendMessage={sendMessage}
          hideMessage={hideMessage}
          activeChatSessionId={activeChatSessionId}
          renderCustomMessage={renderCustomMessage}
          renderCustomMessageLoader={renderCustomMessageLoader}
          renderCustomTemporaryStreamedMessage={renderCustomTemporaryStreamedMessage}
          renderTemporaryMessage={renderTemporaryMessage}
          enableTextStreaming={enableTextStreaming}
          enableContinuousChat={enableContinuousChat}
          setOldestMessageRef={setOldestMessageRef}
          aria-label={chatMessageListAriaLabel}
          recommendedActionsLabel={recommendedActionsLabel}
          customFooter={customFooter}
          feedbackIssueOptions={feedbackIssueOptions}
          onFeedbackSubmitted={onFeedbackSubmitted}
          scrollContainerRef={cdsLabsContainerRef}
        />
      )}
    </>
  );

  return (
    <div css={styles.conversation} className="coach-conversation-container">
      {/*
        If customHeader is undefined we show the default header.
        If a React element is passed for customHeader, the custom header is displayed.
        If customHeader is null, we hide the header.
      */}
      {customHeader === undefined && (
        <div css={styles.headerContainer()}>
          <ChatHeader
            onClose={toggleChat}
            showStartNewChat={showStartNewChat}
            showClose={showCloseButton}
            handleNewSession={handleNewSession}
            handleSettingsClick={handleSettingsClick}
            showSettings={showSettings}
            loading={isChatHistoryLoading || isMessageLoading || isMessageStreaming}
            isCoachStudio={isCoachStudio}
            showFullScreenButton={showFullScreenButton}
            toggleFullScreen={toggleFullScreen}
            isFullScreen={isFullScreen}
            showPositionToggle={showPositionToggle}
            handlePositionToggleClick={handlePositionToggleClick}
            chatPopupButton={chatPopupButton}
            handlePopupButtonClick={handlePopupButtonClick}
            showSearchButton={messagesCount > 0 ? showSearchButton : false}
            searchButtonRef={searchButtonRef}
            settingsButtonRef={settingsButtonRef}
            enableContinuousChat={enableContinuousChat}
            notifications={
              <ChatHeaderNotifications
                enableContinuousChat={enableContinuousChat}
                showSettings={showSettings}
                settingsOpen={settingsOpen}
                isCoachStudio={isCoachStudio}
                isMessageLoading={isMessageLoading}
                memoryNotificationConfig={memoryNotificationConfig}
                enableMemory={enableMemory}
                brandLabel={brandLabel}
              />
            }
          />
        </div>
      )}
      {customHeader !== undefined && customHeader !== null && (
        <div css={styles.headerContainer(true)}>
          {customHeader}
          <ChatHeaderNotifications
            enableContinuousChat={enableContinuousChat}
            showSettings={showSettings}
            settingsOpen={settingsOpen}
            isCoachStudio={isCoachStudio}
            isMessageLoading={isMessageLoading}
            memoryNotificationConfig={memoryNotificationConfig}
            enableMemory={enableMemory}
            brandLabel={brandLabel}
          />
        </div>
      )}

      <Fade in={shouldShowEarlierMessagesButton} timeout={200}>
        <CoachButton
          aria-label={_t('Earlier messages')}
          icon={<ArrowUpIcon />}
          iconPosition="before"
          onClick={handleClickEarlierMessagesButton}
          css={styles.earlierMessagesButton}
          size="small"
          variant="ghost"
          data-testid="earlier-messages-button"
        >
          {_t('Earlier messages')}
        </CoachButton>
      </Fade>

      <ChatMessagesContainer
        ref={cdsLabsContainerRef}
        scrollElementRef={cdsLabsScrollElementRef}
        enableJumpToBottom={messagesCount > 0 && !isMessageLoading && !isMessageStreaming}
        enableJumpToTop={isSearchMode()}
        onJumpToTop={trackJumpToTop}
        onJumpToBottom={trackJumpToBottom}
      >
        {messagesContent}
      </ChatMessagesContainer>

      {!hideComposer && (
        <div
          ref={composerContainerRef}
          css={styles.composerContainer}
          onTransitionEnd={handleTransitionEnd}
          className={classNames({ hidden: composerHidden })}
        >
          <div css={styles.composer}>{composerComponent}</div>
        </div>
      )}
      {!hideFooter && (
        <div css={styles.footerContainer(hideComposer)} className="coach-conversation-footer-container">
          <div css={styles.footer}>{customFooter || <ChatFooter />}</div>
        </div>
      )}
    </div>
  );
};

export default Conversation;
